import React from 'react';
import { Alert, AlertTitle, Button, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import { useNavigate } from 'react-router-dom';

interface PasswordUpgradePromptProps {
  onDismiss: () => void;
}

/**
 * PasswordUpgradePrompt component
 *
 * Displays a dismissible security alert prompting users to update their password
 * to meet the new 12-character minimum requirement (AUTH-VULN-01).
 *
 * Security context:
 * - Part of CVSS 7.5 HIGH severity remediation
 * - Prompts users with weak passwords (<12 chars) to upgrade
 * - Dismissible per session but reappears on next login until password is updated
 *
 * @param onDismiss - Callback to dismiss the prompt for the current session
 */
export const PasswordUpgradePrompt: React.FC<PasswordUpgradePromptProps> = ({ onDismiss }) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/auth/set-password');
    onDismiss();
  };

  return (
    <Alert
      severity="warning"
      icon={<SecurityIcon />}
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button color="inherit" size="small" variant="outlined" onClick={handleUpgrade}>
            Update Password
          </Button>
          <IconButton aria-label="close" color="inherit" size="small" onClick={onDismiss}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Box>
      }
      sx={{ mb: 2 }}
    >
      <AlertTitle>Password Security Update Required</AlertTitle>
      Your password does not meet our updated security requirements (minimum 12 characters). Please
      update your password to keep your account secure.
    </Alert>
  );
};
